import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { api } from '@/App';
import Navbar from '@/components/Navbar';

export default function AdminAuditLogs({ user, onLogout }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterAction, setFilterAction] = useState('all');
  const [filterActorType, setFilterActorType] = useState('all');
  const [filterTargetType, setFilterTargetType] = useState('all');
  const [filterDays, setFilterDays] = useState('30');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadLogs();
  }, [filterAction, filterActorType, filterTargetType, filterDays]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterAction && filterAction !== 'all') params.append('action', filterAction);
      if (filterActorType && filterActorType !== 'all') params.append('actor_type', filterActorType);
      if (filterTargetType && filterTargetType !== 'all') params.append('target_type', filterTargetType);
      params.append('days', filterDays);
      
      const response = await api.get(`/admin/audit-logs?${params.toString()}`);
      setLogs(response.data);
    } catch (error) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const getFilteredLogs = () => {
    if (!searchTerm) return logs;
    
    const term = searchTerm.toLowerCase();
    return logs.filter(log => 
      log.actor_name?.toLowerCase().includes(term) ||
      log.target_name?.toLowerCase().includes(term) ||
      log.action.toLowerCase().includes(term)
    );
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionBadge = (action) => {
    const colors = {
      'GAME_REGISTRATION': 'bg-green-100 text-green-800',
      'GAME_CANCELLATION': 'bg-red-100 text-red-800',
      'JOINED_WAITING_LIST': 'bg-yellow-100 text-yellow-800',
      'GAME_CREATED': 'bg-blue-100 text-blue-800',
      'GAME_UPDATED': 'bg-blue-100 text-blue-800',
      'GAME_DELETED': 'bg-red-100 text-red-800',
      'USER_CREATED': 'bg-purple-100 text-purple-800',
      'USER_APPROVED': 'bg-green-100 text-green-800',
      'USER_REJECTED': 'bg-red-100 text-red-800',
      'USER_LOGIN': 'bg-gray-100 text-gray-800',
      'PAYMENT_MARKED': 'bg-emerald-100 text-emerald-800',
      // Notification actions
      'NOTIFICATION_GAME_CREATED': 'bg-cyan-100 text-cyan-800',
      'NOTIFICATION_GAME_CANCELLED': 'bg-rose-100 text-rose-800',
      'NOTIFICATION_GAME_REMINDER': 'bg-indigo-100 text-indigo-800',
      'NOTIFICATION_REGISTRATION_CONFIRMED': 'bg-teal-100 text-teal-800',
      'NOTIFICATION_WAITING_LIST': 'bg-amber-100 text-amber-800',
      'NOTIFICATION_PROMOTED': 'bg-emerald-100 text-emerald-800',
      'NOTIFICATION_DEMOTED': 'bg-orange-100 text-orange-800',
      'NOTIFICATION_DEREGISTRATION': 'bg-red-100 text-red-800',
      'NOTIFICATION_PAYMENT_REMINDER': 'bg-violet-100 text-violet-800',
      'NOTIFICATION_USER_APPROVED': 'bg-green-100 text-green-800',
      'NOTIFICATION_USER_REJECTED': 'bg-red-100 text-red-800',
      'NOTIFICATION_PASSWORD_RESET': 'bg-blue-100 text-blue-800',
      'NOTIFICATION_ADMIN_NEW_USER': 'bg-purple-100 text-purple-800',
    };
    
    return colors[action] || 'bg-gray-100 text-gray-800';
  };

  const getActorTypeBadge = (actorType) => {
    const colors = {
      'user': 'bg-blue-500',
      'admin': 'bg-purple-500',
      'system': 'bg-gray-500'
    };
    return colors[actorType] || 'bg-gray-500';
  };

  const formatActionLabel = (action) => {
    const labels = {
      'GAME_REGISTRATION': 'Game Registration',
      'GAME_CANCELLATION': 'Game Cancellation',
      'JOINED_WAITING_LIST': 'Joined Waiting List',
      'GAME_CREATED': 'Game Created',
      'GAME_UPDATED': 'Game Updated',
      'GAME_DELETED': 'Game Deleted',
      'USER_CREATED': 'User Created',
      'USER_APPROVED': 'User Approved',
      'USER_REJECTED': 'User Rejected',
      'USER_LOGIN': 'User Login',
      'PAYMENT_MARKED': 'Payment Marked',
      // Notification labels with icons
      'NOTIFICATION_GAME_CREATED': '📧 Game Created Email',
      'NOTIFICATION_GAME_CANCELLED': '📧 Game Cancelled Email',
      'NOTIFICATION_GAME_REMINDER': '📧 Game Reminder (26h)',
      'NOTIFICATION_REGISTRATION_CONFIRMED': '📧 Registration Confirmed',
      'NOTIFICATION_WAITING_LIST': '📧 Waiting List Added',
      'NOTIFICATION_PROMOTED': '📧 Promoted to Registered',
      'NOTIFICATION_DEMOTED': '📧 Demoted to Waiting',
      'NOTIFICATION_DEREGISTRATION': '📧 Deregistration Confirmed',
      'NOTIFICATION_PAYMENT_REMINDER': '📧 Payment Reminder',
      'NOTIFICATION_USER_APPROVED': '📧 User Approved Email',
      'NOTIFICATION_USER_REJECTED': '📧 User Rejected Email',
      'NOTIFICATION_PASSWORD_RESET': '📧 Password Reset Email',
      'NOTIFICATION_ADMIN_NEW_USER': '📧 Admin New User Alert',
    };
    return labels[action] || action.replace(/_/g, ' ');
  };

  const filteredLogs = getFilteredLogs();
  
  // Calculate statistics
  const totalActions = filteredLogs.length;
  const userActions = filteredLogs.filter(l => l.actor_type === 'user').length;
  const adminActions = filteredLogs.filter(l => l.actor_type === 'admin').length;
  const systemActions = filteredLogs.filter(l => l.actor_type === 'system').length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">Loading audit logs...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Audit Logs</h1>
          <p className="text-gray-600">Complete activity history for users, admins, and system</p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg border p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Filters</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Time Period</label>
              <Select value={filterDays} onValueChange={setFilterDays}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Last 24 Hours</SelectItem>
                  <SelectItem value="7">Last 7 Days</SelectItem>
                  <SelectItem value="30">Last 30 Days</SelectItem>
                  <SelectItem value="90">Last 90 Days</SelectItem>
                  <SelectItem value="365">Last Year</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Action Type</label>
              <Select value={filterAction} onValueChange={setFilterAction}>
                <SelectTrigger>
                  <SelectValue placeholder="All Actions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  <SelectItem value="GAME_REGISTRATION">Game Registration</SelectItem>
                  <SelectItem value="GAME_CANCELLATION">Game Cancellation</SelectItem>
                  <SelectItem value="JOINED_WAITING_LIST">Joined Waiting List</SelectItem>
                  <SelectItem value="GAME_CREATED">Game Created</SelectItem>
                  <SelectItem value="GAME_UPDATED">Game Updated</SelectItem>
                  <SelectItem value="USER_CREATED">User Created</SelectItem>
                  <SelectItem value="USER_APPROVED">User Approved</SelectItem>
                  <SelectItem value="PAYMENT_MARKED">Payment Marked</SelectItem>
                  <SelectItem value="NOTIFICATION_GAME_CREATED">📧 Game Created Notification</SelectItem>
                  <SelectItem value="NOTIFICATION_GAME_CANCELLED">📧 Game Cancelled Notification</SelectItem>
                  <SelectItem value="NOTIFICATION_GAME_REMINDER">📧 Game Reminder (26h)</SelectItem>
                  <SelectItem value="NOTIFICATION_REGISTRATION_CONFIRMED">📧 Registration Confirmed</SelectItem>
                  <SelectItem value="NOTIFICATION_WAITING_LIST">📧 Waiting List Added</SelectItem>
                  <SelectItem value="NOTIFICATION_PROMOTED">📧 Promoted</SelectItem>
                  <SelectItem value="NOTIFICATION_DEMOTED">📧 Demoted</SelectItem>
                  <SelectItem value="NOTIFICATION_DEREGISTRATION">📧 Deregistration Confirmed</SelectItem>
                  <SelectItem value="NOTIFICATION_PAYMENT_REMINDER">📧 Payment Reminder</SelectItem>
                  <SelectItem value="NOTIFICATION_USER_APPROVED">📧 User Approved</SelectItem>
                  <SelectItem value="NOTIFICATION_USER_REJECTED">📧 User Rejected</SelectItem>
                  <SelectItem value="NOTIFICATION_PASSWORD_RESET">📧 Password Reset</SelectItem>
                  <SelectItem value="NOTIFICATION_ADMIN_NEW_USER">📧 Admin New User Alert</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Actor Type</label>
              <Select value={filterActorType} onValueChange={setFilterActorType}>
                <SelectTrigger>
                  <SelectValue placeholder="All Actors" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actors</SelectItem>
                  <SelectItem value="user">Users</SelectItem>
                  <SelectItem value="admin">Admins</SelectItem>
                  <SelectItem value="system">System</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Target Type</label>
              <Select value={filterTargetType} onValueChange={setFilterTargetType}>
                <SelectTrigger>
                  <SelectValue placeholder="All Targets" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Targets</SelectItem>
                  <SelectItem value="game">Games</SelectItem>
                  <SelectItem value="user">Users</SelectItem>
                  <SelectItem value="notification">Notifications</SelectItem>
                  <SelectItem value="content">Content</SelectItem>
                  <SelectItem value="system">System</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Search</label>
              <Input
                placeholder="Search names..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg border">
            <div className="text-2xl font-bold text-gray-900">{totalActions}</div>
            <div className="text-sm text-gray-600">Total Actions</div>
          </div>
          <div className="bg-white p-4 rounded-lg border">
            <div className="text-2xl font-bold text-blue-600">{userActions}</div>
            <div className="text-sm text-gray-600">User Actions</div>
          </div>
          <div className="bg-white p-4 rounded-lg border">
            <div className="text-2xl font-bold text-purple-600">{adminActions}</div>
            <div className="text-sm text-gray-600">Admin Actions</div>
          </div>
          <div className="bg-white p-4 rounded-lg border">
            <div className="text-2xl font-bold text-gray-600">{systemActions}</div>
            <div className="text-sm text-gray-600">System Actions</div>
          </div>
        </div>

        {/* Logs Table */}
        {filteredLogs.length === 0 ? (
          <div className="bg-white rounded-lg border p-8 text-center text-gray-500">
            No audit logs found for the selected filters.
          </div>
        ) : (
          <div className="bg-white rounded-lg border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Timestamp
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Action
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actor
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Target
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Details
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredLogs.map((log, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                        {formatDate(log.timestamp)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <Badge className={getActionBadge(log.action)}>
                          {formatActionLabel(log.action)}
                        </Badge>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <Badge className={`${getActorTypeBadge(log.actor_type)} text-white`}>
                            {log.actor_type}
                          </Badge>
                          <span className="text-sm font-medium text-gray-900">
                            {log.actor_name || 'N/A'}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-sm">
                          <div className="font-medium text-gray-900">{log.target_name || 'N/A'}</div>
                          <div className="text-gray-500">{log.target_type}</div>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600">
                        {Object.keys(log.metadata).length > 0 && (
                          <div className="space-y-1">
                            {log.metadata.changes ? (
                              // Show changes with old → new format
                              Object.entries(log.metadata.changes).map(([field, change]) => (
                                <div key={field} className="text-xs bg-gray-50 p-2 rounded">
                                  <span className="font-semibold text-gray-700">{field}:</span>{' '}
                                  <span className="text-red-600">{change.old}</span>
                                  {' → '}
                                  <span className="text-green-600">{change.new}</span>
                                </div>
                              ))
                            ) : (
                              // Show other metadata normally
                              Object.entries(log.metadata).slice(0, 3).map(([key, value]) => (
                                <div key={key} className="text-xs">
                                  <span className="font-medium">{key}:</span> {String(value)}
                                </div>
                              ))
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Info Box */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-semibold text-blue-900 mb-2">📋 About Audit Logs:</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Complete history of all platform actions</li>
            <li>• Logs are automatically deleted after 1 year</li>
            <li>• Includes user actions, admin actions, and system events</li>
            <li>• Use filters to find specific activities or patterns</li>
            <li>• Track cancellations, registrations, and administrative changes</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
